import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext'; 
import PasswordInput from '../components/PasswordInput';


const MONTH_NAMES = [
  'Január', 'Február', 'Március', 'Április', 'Május', 'Június', 
  'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
];

const getSixMonthsFromToday = (date) => {
  const maxDate = new Date(date.getFullYear(), date.getMonth() + 6, 1);
  const lastDayOfMaxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0).getDate();
  maxDate.setDate(Math.min(date.getDate(), lastDayOfMaxMonth));
  return maxDate;
};

export default function Booking() {
  const { user, register, login } = useAuth();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [customDays, setCustomDays] = useState([]);
  
  const [selectedDate, setSelectedDate] = useState('');
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [registrationData, setRegistrationData] = useState({ name: '', phone: '', email: '', password: '' });
  const [registrationError, setRegistrationError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [washingPrice, setWashingPrice] = useState(500);
  const [withWashing, setWithWashing] = useState(false);
  const [slotInterval, setSlotInterval] = useState(20);
  const [openingHours, setOpeningHours] = useState({ open: '08:00', close: '16:00' });

  // A fodrász vagy adminisztrátor nem foglalhat saját magának időpontot.
  const isBarberOrAdmin = user?.role === 'Fodrász' || user?.role === 'Rendszergazda';

  // Szolgáltatások, meglévő foglalások és a rendszerbeállítások betöltése
  useEffect(() => {
    const loadData = async () => {
      try {
        const [servicesRes, bookingsRes, settingsRes, blockedRes, customRes] = await Promise.all([
          axios.get(`/api/services`),
          axios.get(`/api/admin/all-bookings`),
          axios.get(`/api/settings`),
          axios.get(`/api/blocked-dates`),
          axios.get(`/api/custom-days`)
        ]);

        // A felületen lévő hiányos adatok felülírása/helyettesítése
        setServices(servicesRes.data);
        setAllBookings(bookingsRes.data);
        if (settingsRes.data.washing_price) setWashingPrice(parseInt(settingsRes.data.washing_price));
        setSlotInterval(Number(settingsRes.data.slot_interval) || 20);
        setOpeningHours({
          open: settingsRes.data.open_time || '08:00',
          close: settingsRes.data.close_time || '16:00'
        });
        setBlockedDates(blockedRes.data);
        setCustomDays(customRes.data);
      } catch (x) {
        console.error("Hiba az adatok betöltésekor:", x);
      }
    };
    loadData();
  }, [selectedDate, calendarRefreshKey]);

  // A kiválasztott szolgáltatás időtartamának meghatározása
  const getServiceDurationMinutes = (service) => {
    if (!service) return 20;

    if (Number.isFinite(Number(service.duration))) return Number(service.duration);
    if (Number.isFinite(Number(service.duration_minutes))) return Number(service.duration_minutes);
  };

  // Alapértelmezett időtartamok meghatározása
  const getServiceDuration = (service) => {
    const durationMinutes = getServiceDurationMinutes(service);
    return `${durationMinutes} perc`;
  };

  // Szabad időpontok lekérése
  // endH = zárás óra , endM = zárás perc, StartH = nyitás óra, StartM = nyitás perce 
  //slotInterval = 
  const getAvailableSlots = (dStr) => {
    if (!selectedService) return [];

    const serviceDuration = getServiceDurationMinutes(selectedService);
    const custom = customDays.find(d => d.date === dStr);
    const dateObj = new Date(dStr);
    const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
    
    if (isWeekend && !custom) return [];

    const startTime = custom ? custom.start_time : openingHours.open;
    const endTime = custom ? custom.end_time : openingHours.close;
    const startH = parseInt(startTime.split(':')[0]);
    const startM = parseInt(startTime.split(':')[1]);
    const endH = parseInt(endTime.split(':')[0]);
    const endM = parseInt(endTime.split(':')[1]);

    const workStart = startH * 60 + startM;
    const workEnd = endH * 60 + endM;

    const dayBookings = allBookings
      .filter(b => b.date === dStr)
      .map(b => {
        const [h, m] = b.time.split(':').map(Number);
        const start = h * 60 + m;
        const [endH, endM] = (b.endTime || '').split(':').map(Number);
        return {
          start,
          end: Number.isNaN(endH) || Number.isNaN(endM) ? start + slotInterval : endH * 60 + endM
        };
      });

    const slots = [];

    for (let m = workStart; m + serviceDuration <= workEnd; m += slotInterval) {
      const sStart = m;
      const sEnd = m + serviceDuration;
      
      if (sEnd > workEnd) continue;

      const hasOverlap = dayBookings.some(b => sStart < b.end && sEnd > b.start);
      
      if (!hasOverlap) {
        const hrs = String(Math.floor(m / 60)).padStart(2, '0');
        const mins = String(m % 60).padStart(2, '0');
        slots.push(`${hrs}:${mins}`);
      }
    }
    return slots;
  };

  // Szabad időpontok lekérése hónap és szolgáltatás váltásnál.
  useEffect(() => {
    if (selectedDate && selectedService) {
      setAvailableTimes(getAvailableSlots(selectedDate));
    }
  }, [selectedDate, selectedService, allBookings, customDays, openingHours, slotInterval]);

  // Magyar árformázás.
  const formatPrice = (value) => {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) return '0';
    return Math.round(amount).toLocaleString('hu-HU');
  };

  // Kiválasztott időpontból számolt várható befejezési idő.
  const getApproximateEndTime = () => {
    if (!selectedTime || !selectedService) return '';
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const endMinutes = hours * 60 + minutes + getServiceDurationMinutes(selectedService);
    return `${String(Math.floor(endMinutes / 60) % 24).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
  };

  // Szabad időpontok csoportosítása délelőtti és délutáni részre.
  const morningSlots = availableTimes.filter(t => parseInt(t.split(':')[0]) < 12);
  const afternoonSlots = availableTimes.filter(t => parseInt(t.split(':')[0]) >= 12);

  // Végleges foglalás elküldése a backend felé.
  const handleAction = async () => {
    if (!user?.id) {
      setAuthMode('register');
      setRegistrationError('');
      setShowRegistrationPrompt(true);
      return;
    }
    // fodrászként való foglalás tiltása
    if (isBarberOrAdmin) {
      setMessage({ type: 'error', text: 'Fodrászként vagy Adminisztrátorként nem tudsz időpontot foglalni!' });
      return;
    }
    // zárt napon való foglalás tiltása
    if (blockedDates.some(blockedDate => blockedDate.date === selectedDate)) {
      setMessage({ type: 'error', text: 'Erre a napra nem lehet foglalni, mert zárva van!' });
      return;
    }

    setIsSubmitting(true);
    const payload = {
      date: selectedDate,
      time: selectedTime,
      serviceId: selectedService.id,
      customerId: user.id,
      withWashing: withWashing,
      status: 'Függőben'
    };

    try {
      const res = await axios.post(`/api/bookings`, payload);
      if (res.data.success) {
        setMessage({ type: 'success', text: 'Sikeres foglalás!' });
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Hiba történt!' });
      setIsSubmitting(false);
    }
  };

  const handleRegistrationSubmit = async (event) => {
    event.preventDefault();
    setRegistrationError('');

    if (registrationData.phone && /\D/.test(registrationData.phone)) {
      setRegistrationError('A telefonszám mezőbe csak számokat írhatsz be!');
      return;
    }

    setIsRegistering(true);
    try {
      const result = await register(registrationData);
      if (!result.success) {
        setRegistrationError(result.error || 'Hiba történt a regisztráció során.');
        return;
      }

      const loginResult = await login(registrationData.email, registrationData.password);
      if (!loginResult.success) {
        setRegistrationError('A regisztráció sikerült, de az automatikus bejelentkezés nem sikerült.');
        return;
      }

      setShowRegistrationPrompt(false);
      setRegistrationData({ name: '', phone: '', email: '', password: '' });
      setMessage({ type: 'success', text: 'Sikeres regisztráció! Most már véglegesítheted a foglalást.' });
    } catch (error) {
      setRegistrationError('Hálózati hiba történt a regisztráció során.');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);

    try {
      const result = await login(loginData.email, loginData.password);
      if (!result.success) {
        setLoginError(result.error || 'Hibás email cím vagy jelszó.');
        return;
      }

      setShowRegistrationPrompt(false);
      setLoginData({ email: '', password: '' });
      setMessage({ type: 'success', text: 'Sikeres bejelentkezés! Most már véglegesítheted a foglalást.' });
    } catch (error) {
      setLoginError('Hálózati hiba történt a bejelentkezés során.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = new Date(year, month, 1).getDay() === 0 ? 6 : new Date(year, month, 1).getDay() - 1;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxBookingDate = getSixMonthsFromToday(today);
  const isLastBookableMonth = year === maxBookingDate.getFullYear() && month === maxBookingDate.getMonth();

  const [currentStep, setCurrentStep] = useState(1);
  const leftColRef = useRef(null);
  const rightColRef = useRef(null);

  // Magasság szinkronizálása, hogy a bal és jobb oszlop mindig azonos legyen.
  useEffect(() => {
    const syncHeights = () => {
      const left = leftColRef.current;
      const right = rightColRef.current;
      if (!left || !right) return;
      left.style.height = 'auto';
      right.style.height = 'auto';
      const lh = left.getBoundingClientRect().height;
      const rh = right.getBoundingClientRect().height;
      const maxH = Math.max(lh, rh);
      left.style.height = `${maxH}px`;
      right.style.height = `${maxH}px`;
    };

    syncHeights();
    const ro = new ResizeObserver(syncHeights);
    if (leftColRef.current) ro.observe(leftColRef.current);
    if (rightColRef.current) ro.observe(rightColRef.current);
    window.addEventListener('resize', syncHeights);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncHeights);
    };
  }, [selectedDate, selectedService, availableTimes, currentStep, allBookings]);

  return (
    <div className="relative min-h-screen bg-slate-50 dark:bg-[#0b0f19] text-slate-800 dark:text-slate-100 font-sans p-4 sm:p-10 overflow-auto transition-colors duration-300">
      
      {/* Háttér dekoratív fények */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-200/40 dark:bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-indigo-200/40 dark:bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        
        {/* Cím szekció */}
        <div className="text-center mb-12">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-700 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/30 px-3.5 py-1.5 rounded-full backdrop-blur-md">
            Online Időpontfoglalás
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-white mt-4 mb-3">
            Válassz szolgáltatást
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            Böngéssz kínálatunkból, és foglald le a neked megfelelő időpontot néhány kattintással.
          </p>
        </div>

        {/* foglalás aktuális állapota */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="flex items-center">
            <div className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold transition-colors ${currentStep === 1 ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300'}`}>
                1
              </div>
              <div className={`ml-4 text-sm ${currentStep === 1 ? 'text-slate-900 dark:text-white font-semibold' : 'text-slate-500 dark:text-slate-400'}`}>Szolgáltatás</div>
            </div>

            <div className={`${currentStep > 1 ? 'bg-gradient-to-r from-purple-400 to-indigo-400' : 'bg-gray-200 dark:bg-slate-700'} h-0.5 mx-4 flex-1 rounded`} />

            <div className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold transition-colors ${currentStep === 2 ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300'}`}>
                2
              </div>
              <div className={`ml-4 text-sm ${currentStep === 2 ? 'text-slate-900 dark:text-white font-semibold' : 'text-slate-500 dark:text-slate-400'}`}>Időpont</div>
            </div>

            <div className={`${currentStep > 2 ? 'bg-gradient-to-r from-purple-400 to-indigo-400' : 'bg-gray-200 dark:bg-slate-700'} h-0.5 mx-4 flex-1 rounded`} />

            <div className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold transition-colors ${currentStep === 3 ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300'}`}>
                3
              </div>
              <div className={`ml-4 text-sm ${currentStep === 3 ? 'text-slate-900 dark:text-white font-semibold' : 'text-slate-500 dark:text-slate-400'}`}>Befejezés</div>
            </div>
          </div>
        </div>

        {/* Figyelmeztetés */}
        {isBarberOrAdmin && (
          <div className="mb-8 p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl text-amber-500 text-center font-bold text-sm">
            ⚠️ Fodrász/Admin fiókkal vagy bejelentkezve. Ebben a nézetben az időpontfoglalás le van tiltva.
          </div>
        )}

        {/* Navigáció */}
        <div className="max-w-3xl mx-auto mt-8 flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <button
                onClick={() => {
                  if (currentStep === 2) setCurrentStep(1);
                  else if (currentStep === 3) { setSelectedTime(''); setCurrentStep(2); }
                  setCalendarRefreshKey(value => value + 1);
                }}
                className="px-5 py-3 mb-5 rounded-2xl bg-gray-100 dark:bg-slate-800/60 text-slate-800 dark:text-slate-200 font-semibold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all"
              >
                ← Vissza
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {currentStep < 3 && (
              <button 
                onClick={() => {
                  if (currentStep === 1) setCurrentStep(2);
                  else if (currentStep === 2) setCurrentStep(3);
                  setCalendarRefreshKey(value => value + 1);
                }}
                disabled={
                  (currentStep === 1 && !selectedService) ||
                  (currentStep === 2 && !(selectedDate && selectedTime))
                }
                className={`px-6 py-3 mb-5 rounded-2xl font-bold text-white transition-all ${
                  (currentStep === 1 && !selectedService) || (currentStep === 2 && !(selectedDate && selectedTime))
                    ? 'bg-gray-300 dark:bg-slate-800 cursor-not-allowed opacity-60'
                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-lg shadow-purple-500/20'
                }`}
              >

                Következő →
              </button>
            )}

            {currentStep === 3 && (
              <button
                onClick={handleAction}
                disabled={isSubmitting || isBarberOrAdmin}
                className={`px-6 py-3 mb-5 rounded-2xl font-bold text-white transition-all ${isSubmitting || isBarberOrAdmin ? 'bg-slate-700 cursor-not-allowed opacity-50' : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-lg shadow-purple-500/20'}`}
              >
                {isSubmitting ? 'Feldolgozás...' : (isBarberOrAdmin ? 'Fodrászként nem foglalhatsz' : (user ? 'Foglalás Véglegesítése' : 'Regisztráció'))}
              </button>
            )}
          </div>
        </div>

        {/* 1. lépés: szolgáltatás kiválasztása és árlistázás. */}
        {currentStep === 1 && (
          <div className="space-y-6 mb-12">
            {/* Szolgáltatások kártyái */}
            {services.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {services.map(s => {
                  const isSelected = selectedService?.id === s.id;
                  const duration = getServiceDuration(s);
                  return (
                    <button 
                      key={s.id} 
                      onClick={() => {setSelectedService(s); setSelectedTime(''); setSelectedDate('');}} 
                      className={`relative group p-6 rounded-3xl text-left transition-all duration-300 backdrop-blur-xl flex flex-col justify-between ${
                        isSelected 
                          ? 'bg-purple-100/80 dark:bg-[#18152e] border-2 border-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.35)]' 
                          : 'bg-white/80 dark:bg-slate-900/60 border border-purple-200/60 dark:border-purple-500/30 hover:border-purple-400 dark:hover:border-purple-400 hover:bg-white dark:hover:bg-slate-900/90 hover:shadow-lg'
                      }`}
                    >
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          {/* Időtartam (Badge) */}
                          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/20 text-xs font-semibold">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{duration}</span>
                          </div>

                          {isSelected && (
                            <span className="flex h-3 w-3 relative">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-600 dark:bg-purple-500"></span>
                            </span>
                          )}
                        </div>

                        <p className="text-lg font-bold text-slate-900 dark:text-white mb-1 group-hover:text-purple-600 dark:group-hover:text-purple-300 transition-colors">
                          {s.name}
                        </p>

                        {s.description && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 line-clamp-2">
                            {s.description}
                          </p>
                        )}
                      </div>

                      <div className="pt-3 mt-4 border-t border-slate-100 dark:border-purple-500/20 flex items-end justify-between">
                        <div>
                          <span className="text-[10px] uppercase text-slate-400 block font-semibold">Ár</span>
                          <p className="text-2xl font-black text-purple-700 dark:bg-gradient-to-r dark:from-purple-400 dark:to-blue-400 dark:bg-clip-text dark:text-transparent">
                            {formatPrice(s.price)} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">Ft</span>
                          </p>
                        </div>
                        <span className={`text-xs font-bold px-3 py-1.5 rounded-xl transition-all ${isSelected ? 'bg-purple-600 text-white' : 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 group-hover:bg-purple-600 group-hover:text-white'}`}>
                          {isSelected ? 'Kiválasztva ✓' : 'Kiválasztás'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-white/50 dark:bg-slate-900/40 rounded-3xl border border-slate-200 dark:border-slate-800">
                <p className="text-slate-500 dark:text-slate-400 text-sm">Nincs elérhető szolgáltatás.</p>
              </div>
            )}
          </div>
        )}

        {/* 2. és 3. lépés: naptár, időpont-választás és összegzés. */}
        {currentStep >= 2 && (
          <div className="grid lg:grid-cols-12 gap-8 items-stretch animate-fade-in">
            {/* Naptár kártya */}
            {currentStep !== 3 && (
              <div className="lg:col-span-7 h-full bg-white/70 dark:bg-slate-900/60 p-6 sm:p-8 rounded-3xl border border-slate-200/80 dark:border-purple-500/20 shadow-sm flex flex-col">
                <div className="flex justify-between items-center mb-4">
                  <button 
                    onClick={() => {
                      setCurrentMonth(new Date(year, month - 1));
                      setCalendarRefreshKey(value => value + 1);
                    }}
                    className="p-2.5 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800/80 dark:hover:bg-slate-700/80 border border-slate-200 dark:border-purple-500/30 transition-all text-xs font-semibold text-slate-700 dark:text-slate-300"
                  >
                    ← Előző
                  </button>
                  <span className="font-bold text-base tracking-wide text-slate-900 dark:text-white">
                    {year}. {MONTH_NAMES[month]}
                  </span>
                  <button 
                    onClick={() => {
                      setCurrentMonth(new Date(year, month + 1));
                      setCalendarRefreshKey(value => value + 1);
                    }}
                    disabled={isLastBookableMonth || new Date(year, month + 1, 1) > maxBookingDate}
                    className="p-2.5 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800/80 dark:hover:bg-slate-700/80 border border-slate-200 dark:border-purple-500/30 transition-all text-xs font-semibold text-slate-700 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Következő →
                  </button>
                </div>

                <div className="flex-1" ref={leftColRef}>
                  <div className="grid grid-cols-7 gap-2 mb-2">
                    {['H','K','Sze','Cs','P','Szo','V'].map((d, i) => (
                      <div key={i} className="text-center text-xs font-bold text-slate-400 dark:text-slate-500 py-2">
                        {d}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-2">
                    {[...Array(offset)].map((_, i) => <div key={`off-${i}`} />)}
                    {[...Array(daysInMonth)].map((_, i) => {
                      const day = i + 1;
                      const dStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const dateObj = new Date(year, month, day);
                      
                      const isPastOrToday = dateObj <= today;
                      const isBeyondBookingWindow = dateObj > maxBookingDate;
                      const isBlocked = blockedDates.some(d => d.date === dStr);
                      const hasCustom = customDays.some(d => d.date === dStr);
                      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                      
                      const isActuallyClosed = isBlocked || (isWeekend && !hasCustom);
                      const availableForDay = getAvailableSlots(dStr);
                      const isFull = !isActuallyClosed && !isPastOrToday && availableForDay.length === 0;
                      const isDisabled = isPastOrToday || isBeyondBookingWindow || isActuallyClosed || isFull;
                      // múlt/ma , zárva, tele, egyedi
                      // dStr = dátum
                      let btnStyle = "bg-emerald-200 hover:bg-purple-100 hover:border-purple-400 dark:bg-emerald-800 dark:hover:bg-purple-600/30 dark:hover:border-purple-400 text-slate-700 dark:text-slate-200 border border-slate-200/80 dark:border-purple-500/20";
                      if (isPastOrToday) btnStyle = "opacity-30 cursor-not-allowed bg-slate-100 dark:bg-slate-900/20 border-transparent";
                      else if (isBeyondBookingWindow) btnStyle = "opacity-30 cursor-not-allowed bg-slate-100 dark:bg-slate-900/20 border-transparent";
                      else if (isActuallyClosed) btnStyle = "bg-rose-50 text-rose-500 border border-rose-200 dark:bg-rose-950/20 dark:text-rose-500/70 dark:border-rose-900/30 cursor-not-allowed line-through";
                      else if (isFull) btnStyle = "bg-slate-200/80 text-red-500 border border-slate-300 dark:bg-slate-600/60 dark:text-red-500 dark:border-slate-500 cursor-not-allowed";
                      else if (selectedDate === dStr) btnStyle = "bg-purple-600 text-white font-bold border-2 border-purple-600 dark:border-purple-400 shadow-md shadow-purple-500/20";
                      else if (hasCustom) btnStyle = "border border-amber-400 hover:bg-purple-100 hover:border-purple-400 text-amber-700 bg-amber-50 dark:border-amber-500/40 dark:hover:bg-purple-600/30 dark:hover:border-purple-400 dark:text-amber-400 dark:bg-amber-500/10";

                      return (
                        <button 
                          key={day} 
                          onClick={() => {if (!isDisabled) { setSelectedDate(dStr); setSelectedTime(''); }}} 
                          disabled={isDisabled} 
                          className={`h-11 rounded-2xl font-medium text-xs sm:text-sm transition-all duration-200 flex items-center justify-center ${btnStyle}`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                  {/* napok színének jelentése a naptárban */}
                  <div className="mt-8 pt-6 border-t border-slate-200/80 dark:border-purple-500/20 flex flex-wrap gap-4 text-xs text-slate-500 dark:text-slate-400 justify-center">
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 dark:bg-emerald-500"></span> Elérhető</div>
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-purple-600 dark:bg-purple-500"></span> Kiválasztva</div>
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 dark:bg-amber-500/70"></span> Egyedi nyitva tartás</div>
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-500"></span> Betelt</div>
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-rose-400 dark:bg-rose-500/70"></span> Zárva</div>
                  </div>
                </div>
              </div>
            )}

            {/* Időpontok és összefoglaló panel, a kiválasztott adatok feldolgozásához. */}
            <div ref={rightColRef} className={`${currentStep !== 3 ? 'lg:col-span-5 h-full flex flex-col gap-6' : 'lg:col-span-12'} h-full`}> 
              {currentStep !== 3 && (
                <>
                  {selectedDate ? (
                    <>
                      <div className="p-5 bg-white/70 dark:bg-slate-900/60 border border-slate-200/80 dark:border-purple-500/20 rounded-3xl flex items-center justify-between shadow-sm flex-1 min-h-[110px]">
                        <div>
                          <p className="font-semibold text-sm text-slate-900 dark:text-white">Mosással kéred?</p>
                          <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">+{formatPrice(washingPrice)} Ft extra</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" checked={withWashing} onChange={(e) => setWithWashing(e.target.checked)} />
                          <div className="w-11 h-6 bg-slate-200 dark:bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:after:bg-slate-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 peer-checked:after:bg-white"></div>
                        </label>
                      </div>

                      <div className="bg-white/70 dark:bg-slate-900/60 p-6 rounded-3xl border border-slate-200/80 dark:border-purple-500/20 shadow-sm space-y-6 flex-1 flex flex-col">
                        <div>
                          <h3 className="text-xs font-bold uppercase tracking-wider text-purple-700 dark:text-purple-400/80 mb-3">
                            Délelőtt ({morningSlots.length})
                          </h3>
                          {morningSlots.length > 0 ? (
                            <div className="grid grid-cols-3 gap-2.5">
                              {morningSlots.map(t => (
                                <button 
                                  key={t} 
                                  onClick={() => setSelectedTime(t)} 
                                  className={`py-2.5 rounded-xl font-medium text-xs border transition-all duration-200 ${
                                    selectedTime === t 
                                      ? 'bg-purple-600 text-white border-purple-600 shadow-md shadow-purple-500/20' 
                                      : 'bg-slate-100/70 border-slate-200 hover:border-purple-400 dark:bg-slate-800/50 dark:border-purple-500/20 dark:hover:border-purple-500/50 text-slate-700 dark:text-slate-300'
                                  }`}
                                >
                                  {t}
                                </button>
                              ))}
                            </div>
                          ) : <p className="text-slate-400 text-xs italic">Nincs szabad időpont</p>}
                        </div>

                        <div>
                          <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-blue-400/80 mb-3">
                            Délután ({afternoonSlots.length})
                          </h3>
                          {afternoonSlots.length > 0 ? (
                            <div className="grid grid-cols-3 gap-2.5">
                              {afternoonSlots.map(t => (
                                <button 
                                  key={t} 
                                  onClick={() => setSelectedTime(t)} 
                                  className={`py-2.5 rounded-xl font-medium text-xs border transition-all duration-200 ${
                                    selectedTime === t 
                                      ? 'bg-purple-600 text-white border-purple-600 shadow-md shadow-purple-500/20' 
                                      : 'bg-slate-100/70 border-slate-200 hover:border-purple-400 dark:bg-slate-800/50 dark:border-purple-500/20 dark:hover:border-purple-500/50 text-slate-700 dark:text-slate-300'
                                  }`}
                                >
                                  {t}
                                </button>
                              ))}
                            </div>
                          ) : <p className="text-slate-400 text-xs italic">Nincs szabad időpont</p>}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="h-full flex flex-col gap-6">
                      <div className="p-5 bg-white/70 dark:bg-slate-900/60 border border-slate-200/80 dark:border-purple-500/20 rounded-3xl flex items-center justify-center shadow-sm flex-1">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Válassz napot a naptárból az időpontok megjelenítéséhez</p>
                      </div>
                      <div className="bg-white/70 dark:bg-slate-900/60 p-6 rounded-3xl border border-slate-200/80 dark:border-purple-500/20 shadow-sm flex items-center justify-center flex-1">
                        <p className="text-sm text-slate-400">Időpontok ideiglenesen üresek</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Végső összegzés és a megerősítéshez szükséges adatok bemutatása. */}
              {currentStep === 3 && selectedService && (
                <div className="p-8 bg-white/90 dark:bg-gradient-to-b dark:from-purple-900 dark:to-slate-900 border border-purple-200/60 dark:border-purple-500/50 rounded-3xl shadow-2xl shadow-purple-200/30 dark:shadow-black/30 space-y-6 text-slate-800 dark:text-white max-w-xl mx-auto">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-purple-600 dark:text-purple-300">Foglalás Összegzés</span>
                    <h4 className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{selectedService.name}</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">
                      📅 {selectedDate.replace(/-/g, '. ')} • ⏰ {selectedTime}
                    </p>
                    <p className="text-xs font-medium text-purple-600/80 dark:text-purple-300/80 mt-1 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Várható időtartam: {getServiceDuration(selectedService)}, 
                    Várható befejezés: {getApproximateEndTime()}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    </p>
                  </div>

                  <div className="space-y-2 border-t border-purple-200 dark:border-purple-500/30 pt-4">
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>Alapszolgáltatás:</span>
                      <span>{formatPrice(selectedService.price)} Ft</span>
                    </div>
                    {withWashing && (
                      <div className="flex justify-between text-xs text-purple-600 dark:text-purple-400">
                        <span>Hajmosás:</span>
                        <span>+{formatPrice(washingPrice)} Ft</span>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center border-t border-purple-200 dark:border-purple-500/30 pt-4">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Fizetendő összeg:</span>
                    {/* A végösszeghez a mosás díja csak a mosási opció kiválasztása után adódik hozzá. */}
                    <span className="text-3xl font-black text-slate-900 dark:text-white">
                      {formatPrice(Number(selectedService.price) + (withWashing ? Number(washingPrice) : 0))} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">Ft</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>  
        )}

        {/* Regisztrációs / bejelentkezési prompt, ha nincs bejelentkezve. */}
        {showRegistrationPrompt && (
          <div className="fixed inset-0 z-[210] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="registration-prompt-title"
              className="w-full max-w-md rounded-[2rem] border border-white/20 bg-white p-7 shadow-2xl dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="mb-5 flex items-center justify-between">
                <h2 id="registration-prompt-title" className="text-xl font-black text-gray-900 dark:text-white">
                  {authMode === 'register' ? 'Regisztráció' : 'Bejelentkezés'}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowRegistrationPrompt(false)}
                  aria-label="A regisztrációs ablak bezárása"
                  className="text-2xl leading-none text-gray-400 transition-colors hover:text-gray-700 dark:text-gray-500 dark:hover:text-white"
                >
                  ×
                </button>
              </div>

              <p className="mb-5 text-sm font-semibold leading-6 text-gray-600 dark:text-gray-300">
                {authMode === 'register'
                  ? 'A foglalás véglegesítéséhez először hozz létre egy fiókot.'
                  : 'Jelentkezz be a foglalás véglegesítéséhez.'}
              </p>

              {/* Mindig az éppen kiválasztott művelethez tartozó hibaüzenet látszik. */}
              {(authMode === 'register' ? registrationError : loginError) && (
                <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
                  {authMode === 'register' ? registrationError : loginError}
                </div>
              )}

              <form onSubmit={authMode === 'register' ? handleRegistrationSubmit : handleLoginSubmit} className="space-y-4">
                {authMode === 'register' ? (
                  <>
                    <input
                      type="text"
                      required
                      placeholder="Teljes név"
                      value={registrationData.name}
                      onChange={(event) => setRegistrationData({ ...registrationData, name: event.target.value })}
                      className="w-full rounded-xl border-2 border-gray-100 p-3 font-semibold outline-none transition-colors focus:border-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <input
                      type="text"
                      placeholder="Telefonszám"
                      value={registrationData.phone}
                      onChange={(event) => setRegistrationData({ ...registrationData, phone: event.target.value })}
                      className="w-full rounded-xl border-2 border-gray-100 p-3 font-semibold outline-none transition-colors focus:border-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </>
                ) : null}
                
                <input
                  type="email"
                  required
                  placeholder="Email cím"
                  value={authMode === 'register' ? registrationData.email : loginData.email}
                  onChange={(event) => authMode === 'register'
                    ? setRegistrationData({ ...registrationData, email: event.target.value })
                    : setLoginData({ ...loginData, email: event.target.value })}
                  className="w-full rounded-xl border-2 border-gray-100 p-3 font-semibold outline-none transition-colors focus:border-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <PasswordInput
                  required
                  minLength="6"
                  placeholder="Jelszó"
                  value={authMode === 'register' ? registrationData.password : loginData.password}
                  onChange={(event) => authMode === 'register'
                    ? setRegistrationData({ ...registrationData, password: event.target.value })
                    : setLoginData({ ...loginData, password: event.target.value })}
                  className="w-full rounded-xl border-2 border-gray-100 p-3 font-semibold outline-none transition-colors focus:border-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowRegistrationPrompt(false)}
                    className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-black text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    Mégse
                  </button>
                  <button
                    type="submit"
                    disabled={isRegistering || isLoggingIn}
                    className="rounded-xl bg-[#a855f7] px-5 py-3 text-sm font-black text-white shadow-lg transition-colors hover:bg-[#9333ea] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {(isRegistering || isLoggingIn)
                      ? 'Folyamatban...'
                      : (authMode === 'register' ? 'Regisztráció' : 'Bejelentkezés')}
                  </button>
                </div>
                {authMode === 'register' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode('login');
                      setLoginError('');
                    }}
                    className="w-full pt-3 text-center text-xs font-black uppercase tracking-wide text-blue-600 transition-colors hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Már van fiókom
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode('register');
                      setRegistrationError('');
                    }}
                    className="w-full pt-3 text-center text-xs font-black uppercase tracking-wide text-blue-600 transition-colors hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Regisztráció
                  </button>
                )}
              </form>
            </div>
          </div>
        )}

        {/* Sikeres vagy hibás foglalási műveletet jelző felugró értesítés. */}
        {message.text && (
          <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3.5 rounded-2xl text-white font-medium text-xs shadow-2xl backdrop-blur-xl border z-50 transition-all ${
            message.type === 'success' 
              ? 'bg-emerald-900/90 border-emerald-500/50 text-emerald-100' 
              : 'bg-rose-900/90 border-rose-500/50 text-rose-100'
          }`}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
}